/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { registerCompanyScopeInvalidator } from './company-scope-lifecycle.utils';

interface ScopedMemoryCacheOptions {
  ttlMs: number;
  maxEntries: number;
}

interface ScopedMemoryCacheEntry<T> {
  value: T;
  expiresAt: number;
}

export interface ScopedMemoryCache<T> {
  read: (key: string) => T | null;
  write: (key: string, value: T) => void;
  clear: () => void;
}

const scopedMemoryCacheClearers = new Set<() => void>();

/** Tenant/workspace değişiminde bütün request-scope bellek cache'lerini temizler. */
export function clearScopedMemoryCaches(): void {
  scopedMemoryCacheClearers.forEach((clear) => clear());
}

registerCompanyScopeInvalidator(clearScopedMemoryCaches);

export function createScopedMemoryCache<T>({
  ttlMs,
  maxEntries,
}: ScopedMemoryCacheOptions): ScopedMemoryCache<T> {
  const entries = new Map<string, ScopedMemoryCacheEntry<T>>();

  const cache: ScopedMemoryCache<T> = {
    read(key) {
      const entry = entries.get(key);
      if (!entry || entry.expiresAt <= Date.now()) return null;
      return entry.value;
    },
    write(key, value) {
      entries.delete(key);
      entries.set(key, {
        value,
        expiresAt: Date.now() + ttlMs,
      });
      while (entries.size > maxEntries) {
        const oldestKey = entries.keys().next().value as string | undefined;
        if (!oldestKey) break;
        entries.delete(oldestKey);
      }
    },
    clear() {
      entries.clear();
    },
  };
  scopedMemoryCacheClearers.add(cache.clear);
  return cache;
}

export function createScopedMemoryCacheKey(namespace: string, scope: unknown): string {
  return `${namespace}:${JSON.stringify(scope)}`;
}
